import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Users, Gem } from 'lucide-react'
import { crashApi } from '../services/crashApi'
import useAppStore from '../../../store/useAppStore'
import { hundredsToX, formatTimeAgo } from '../utils/crashHelpers'

/**
 * Экран истории раундов краша (§9a).
 *
 * Две вкладки на одном компоненте: «Все» (GET /api/crash/history/) и «Мои»
 * (GET /api/crash/history/me/). Пагинация КУРСОРОМ (next_before), не страницей —
 * раунды идут каждые ~20с, при offset-пагинации страницы разъезжались бы.
 *
 * Сокетная crash:history — только 20 множителей в памяти процесса для ленты сверху,
 * экран истории строится по REST.
 */
const CrashHistory = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)
  const requestId = useRef(0)

  const [tab, setTab] = useState('all')   // 'all' | 'me'
  const [rounds, setRounds] = useState([])
  const [nextBefore, setNextBefore] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [error, setError] = useState('')

  const loadPage = useCallback(async (cursor = null, resetList = false) => {
    if (isGuest && tab === 'me') return
    const currentRequest = ++requestId.current
    setIsLoading(true)
    setError('')
    try {
      const data = tab === 'all'
        ? await crashApi.getHistory(cursor)
        : await crashApi.getMyHistory(cursor)
      if (currentRequest !== requestId.current || (tab === 'me' && useAppStore.getState().isGuest)) return
      const list = Array.isArray(data.rounds) ? data.rounds : []
      setRounds((prev) => resetList ? list : [...prev, ...list])
      setNextBefore(data.next_before || null)
    } catch (e) {
      if (currentRequest !== requestId.current || (tab === 'me' && useAppStore.getState().isGuest)) return
      console.error('Crash history load error:', e)
      setError(t('crash.history.loadError'))
    } finally {
      if (currentRequest === requestId.current) {
        setIsLoading(false)
        setIsInitialLoading(false)
      }
    }
  }, [tab, t, isGuest])

  // При смене вкладки — перезагружаем
  useEffect(() => {
    requestId.current += 1
    if (isGuest && tab === 'me') {
      setTab('all')
      return
    }
    setIsInitialLoading(true)
    setRounds([])
    setNextBefore(null)
    loadPage(null, true)
    return () => { requestId.current += 1 }
  }, [tab, loadPage, isGuest])

  const hasMore = Boolean(nextBefore)

  return (
    <div className="px-4 py-6 pb-20 lg:px-6 lg:pb-8">
      <div className="mx-auto max-w-3xl space-y-4">
        {/* Шапка с кнопкой «Назад» */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/crash')}
            className="p-2 rounded-full bg-surface border border-white/10 hover:text-white text-text-secondary"
            aria-label={t('crash.history.back')}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold uppercase italic">
            {t('crash.history.title')}
          </h1>
        </div>

        {/* Вкладки */}
        <div className="grid grid-cols-2 gap-2">
          {[
            { id: 'all', label: t('crash.history.tabs.all') },
            { id: 'me', label: t('crash.history.tabs.my') }
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => {
                if (isGuest && opt.id === 'me') openLogin()
                else setTab(opt.id)
              }}
              className={`
                py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98]
                ${tab === opt.id
                  ? 'bg-accent-primary text-dark-primary'
                  : 'bg-surface text-text-secondary border border-white/10'}
              `}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Содержимое */}
        {isInitialLoading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 rounded-xl bg-surface border border-white/10 animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-400">
            {error}
            <button
              onClick={() => loadPage(null, true)}
              className="block mx-auto mt-3 px-4 py-2 rounded-lg bg-surface border border-white/10"
            >
              {t('crash.history.retry')}
            </button>
          </div>
        ) : rounds.length === 0 ? (
          <div className="text-center py-12 text-text-secondary">
            {t('crash.history.empty')}
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {rounds.map((round) => (
                <HistoryRow key={round.id} round={round} onClick={() => navigate(`/crash/history/${round.id}`)} t={t} />
              ))}
            </div>

            {/* Подгрузка ещё */}
            {hasMore && (
              <button
                onClick={() => loadPage(nextBefore, false)}
                disabled={isLoading}
                className="w-full py-3 rounded-xl bg-surface text-text-secondary hover:text-white border border-white/10 active:scale-[0.98] disabled:opacity-50"
              >
                {isLoading ? t('crash.history.loading') : t('crash.history.loadMore')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const HistoryRow = ({ round, onClick, t }) => {
  // crash_point в сотых (15747 → 157.47×)
  const crashPoint = Number(round.crash_point || 0)
  const isLow = crashPoint < 200

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 rounded-xl bg-surface border border-white/10 hover:border-white/20 active:scale-[0.99] transition-all text-left"
    >
      {/* Точка краха */}
      <div
        className={`
          shrink-0 w-16 py-2 rounded-lg text-center font-bold tabular-nums
          ${isLow
            ? 'bg-red-500/15 text-red-400 border border-red-500/30'
            : 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'}
        `}
      >
        {hundredsToX(crashPoint)}×
      </div>

      {/* Инфо */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 text-xs text-text-secondary">
          <span className="inline-flex items-center gap-1">
            <Users className="w-3 h-3" />
            {Number(round.players || 0)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Gem className="w-3 h-3" />
            {Number(round.sum_bet || 0).toLocaleString('ru-RU')}
          </span>
        </div>
        <div className="text-xs text-text-secondary mt-0.5">
          {formatTimeAgo(round.finished_sec_ago, t)}
        </div>
        {/* Свой результат, если был */}
        {round.you && (
          <div className="text-xs mt-0.5">
            <span className={round.you.status === 'won' ? 'text-accent-primary' : round.you.status === 'lost' ? 'text-red-400' : 'text-text-secondary'}>
              {round.you.status === 'won' && `+${Number(round.you.profit || 0).toLocaleString('ru-RU')}`}
              {round.you.status === 'lost' && `−${Number(round.you.bet || 0).toLocaleString('ru-RU')}`}
              {round.you.status === 'refunded' && t('crash.history.refunded')}
              {round.you.status === 'placed' && t('crash.history.placed')}
            </span>
          </div>
        )}
      </div>
    </button>
  )
}

export default CrashHistory
